import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Dropdown, Modal, Checkbox, Avatar, message } from 'antd';
import { PlusOutlined, MoreOutlined, DownOutlined, CheckOutlined, UserOutlined } from '@ant-design/icons';
import CommonTable from '../../components/commonTable';
import { SUBTEAMS, SUBTEAM_MEMBERS, SubteamMember, ALL_TEAM_MEMBERS } from '../leadTeam/subteamsData';
import { LeadTeamRoute } from '../../routes/routepath';
import '../leadTeam/styles.scss';

const SubteamDetail = () => {
  const navigate = useNavigate();
  const { subteamId } = useParams();
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [selectedMemberKeys, setSelectedMemberKeys] = useState<string[]>([]);

  const subteam = SUBTEAMS.find(s => s.key === subteamId);
  const members = subteamId ? SUBTEAM_MEMBERS[subteamId] || [] : [];

  // Roster shown in the picker — org-wide list plus any subteam members not already in it.
  const memberPool = useMemo(() => {
    const extras = members.filter(m => !ALL_TEAM_MEMBERS.some(p => p.key === m.key));
    return [...ALL_TEAM_MEMBERS, ...extras];
  }, [members]);

  useEffect(() => {
    if (addMembersOpen) setSelectedMemberKeys(members.map(m => m.key));
  }, [addMembersOpen, members]);

  const toggleMember = (key: string) => {
    setSelectedMemberKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const memberColumns = [
    {
      title: (
        <span className="lt-col-title">
          NAME <DownOutlined style={{ fontSize: 11 }} />
        </span>
      ),
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: SubteamMember, index: number) => (
        <div className="lt-submember-name-cell">
          <span className="lt-subteam-index">{index + 1}</span>
          <div>
            <div className="lt-submember-name">{text}</div>
            <div className="lt-submember-email">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'ACCOUNT STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (v: string) =>
        v === 'Active' ? (
          <span className="lt-submember-status active"><CheckOutlined /> Active</span>
        ) : (
          <span className="lt-submember-status">{v}</span>
        ),
    },
    {
      title: 'SUBTEAM PERMISSIONS',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (perms: string[]) =>
        perms.length ? (
          <div className="lt-submember-permissions">
            {perms.map((p, i) => <div key={i}>{p}</div>)}
          </div>
        ) : '-',
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      align: 'right' as const,
      render: () => <MoreOutlined className="lt-subteam-more" />,
    },
  ];

  if (!subteam) {
    return (
      <div className="lt-page">
        <div className="lt-placeholder">
          Subteam not found. <span className="lt-fallback-link" onClick={() => navigate(LeadTeamRoute)}>Back to Team</span>
        </div>
      </div>
    );
  }

  const optionsMenu = {
    items: [
      { key: 'edit', label: 'Edit subteam name' },
      { type: 'divider' as const },
      { key: 'delete', label: 'Delete subteam', danger: true },
    ],
    onClick: ({ key }: { key: string }) => {
      setOptionsOpen(false);
      message.info(key === 'edit' ? 'Edit subteam — coming soon' : 'Delete subteam — coming soon');
    },
  };

  return (
    <div className="lt-page">
      <div className="lt-subteam-detail">
        <div className="lt-subteam-detail-head">
          <h1 className="lt-title">{subteam.name}</h1>
          <Dropdown
            menu={optionsMenu}
            trigger={['click']}
            open={optionsOpen}
            onOpenChange={setOptionsOpen}
            overlayClassName="lt-subteam-options-menu"
            placement="bottomRight"
          >
            <button className={`lt-options-btn${optionsOpen ? ' lt-options-btn--open' : ''}`}>
              Options <MoreOutlined />
            </button>
          </Dropdown>
        </div>

        <div className="lt-subteams">
          <div className="lt-subteams-head">
            <p className="lt-subteams-desc">
              Team members added to this subteam will not be able to view clients assigned to other subteam
              members unless they are granted permission to 'View clients assigned to {subteam.name}'.
            </p>
            <button className="lt-create-subteam-btn" onClick={() => setAddMembersOpen(true)}>
              <PlusOutlined /> ADD MEMBERS
            </button>
          </div>

          <div className="lt-subteams-count">{subteam.members} MEMBERS</div>

          <CommonTable
            columns={memberColumns}
            dataSource={members}
            rowKey="key"
            pagination={false}
            className="lt-subteams-table"
          />
        </div>
      </div>

      <Modal
        open={addMembersOpen}
        onCancel={() => setAddMembersOpen(false)}
        footer={null}
        width={640}
        className="lt-add-members-modal"
      >
        <h2 className="am-title">Add Members to Subteam</h2>
        <p className="am-subtitle">{selectedMemberKeys.length} team members selected</p>
        <div className="am-list">
          {memberPool.map(m => (
            <div key={m.key} className="am-row" onClick={() => toggleMember(m.key)}>
              <Avatar size={40} icon={<UserOutlined />} className="am-avatar" />
              <div className="am-info">
                <div className="am-name">{m.name}</div>
                <div className="am-email">{m.email}</div>
              </div>
              <Checkbox
                checked={selectedMemberKeys.includes(m.key)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggleMember(m.key)}
              />
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default SubteamDetail;
